import React, { useMemo } from "react";
import {
    allDayBlockClasses,
    displayDurationMinutes,
    displayId,
    displayLabel,
    displayTestId,
    displayTimeStr,
    timedAccentClasses,
    timedBlockClasses,
} from "./calendar-item-display";
import { CalendarItem } from "./calendar-types";
import {
    computeHourWindow,
    computeOverlapLayout,
    formatDateKey,
    formatTime,
    hourLabels,
    isSameDay,
    parseTimeToRow,
    SLOTS_PER_HOUR,
    SUBROWS_PER_HOUR,
    SUBROWS_PER_SLOT,
    tourDurationToSpan,
} from "./calendar-utils";

interface DayGridProps {
    day: Date;
    today: Date;
    eventsByDate: Map<string, CalendarItem[]>;
    onEventClick: (event: CalendarItem) => void;
}

const DAY_ABBREVS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

export const DayGrid: React.FC<DayGridProps> = ({
    day,
    today,
    eventsByDate,
    onEventClick,
}) => {
    const dateKey = formatDateKey(day);
    const isToday = isSameDay(day, today);

    const { allDayEvents, timedEvents } = useMemo(() => {
        const dayEvents = eventsByDate.get(dateKey) || [];
        const allDay: CalendarItem[] = [];
        const timed: CalendarItem[] = [];
        for (const evt of dayEvents) {
            if (displayTimeStr(evt) !== null) {
                timed.push(evt);
            } else {
                allDay.push(evt);
            }
        }
        return { allDayEvents: allDay, timedEvents: timed };
    }, [eventsByDate, dateKey]);

    const hourWindow = useMemo(
        () =>
            computeHourWindow(
                timedEvents.map(evt => ({
                    timeStr: displayTimeStr(evt),
                    durationMinutes: displayDurationMinutes(evt),
                })),
            ),
        [timedEvents],
    );
    const labels = hourLabels(hourWindow);

    const laidOutEvents = useMemo(() => {
        const withRows = timedEvents
            .map(evt => {
                const row = parseTimeToRow(displayTimeStr(evt), hourWindow);
                if (row === null) return null;
                const minutes = displayDurationMinutes(evt);
                return {
                    event: evt,
                    row,
                    span: tourDurationToSpan(minutes, row, hourWindow),
                };
            })
            .filter(
                (
                    item,
                ): item is {
                    event: CalendarItem;
                    row: number;
                    span: number;
                } => item !== null,
            );
        return computeOverlapLayout(withRows);
    }, [timedEvents, hourWindow]);

    return (
        <div className="isolate flex flex-auto flex-col overflow-auto bg-white border border-gray-200 rounded-lg">
            <div className="sticky top-0 z-30 bg-white border-b border-gray-200">
                <div className="flex items-center">
                    <div className="w-14 flex-none flex flex-col items-center py-2">
                        <span className="text-xs font-medium text-gray-500">
                            {DAY_ABBREVS[day.getDay()]}
                        </span>
                        <span
                            className={`mt-0.5 flex h-8 w-8 items-center justify-center rounded-full text-lg font-semibold ${
                                isToday ? "bg-brand-600 text-white" : "text-gray-900"
                            }`}>
                            {day.getDate()}
                        </span>
                    </div>
                    {allDayEvents.length > 0 && (
                        <div className="flex-1 min-w-0 py-1 pr-2 space-y-1">
                            {allDayEvents.map(evt => (
                                <button
                                    key={displayId(evt)}
                                    onClick={() => onEventClick(evt)}
                                    data-testid={displayTestId(evt)}
                                    className={`block w-full text-left px-2 py-1.5 text-sm rounded truncate ${allDayBlockClasses(evt)}`}>
                                    {displayLabel(evt)}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="flex max-w-full flex-none flex-col">
                <div className="flex flex-auto">
                    <div className="sticky left-0 z-10 w-14 flex-none bg-white ring-1 ring-gray-100" />
                    <div className="grid flex-auto grid-cols-1 grid-rows-1 min-w-0">
                        <div
                            style={{
                                gridTemplateRows: `repeat(${labels.length * SLOTS_PER_HOUR}, minmax(1.5rem, 1fr))`,
                            }}
                            className="col-start-1 col-end-2 row-start-1 grid divide-y divide-gray-100">
                            <div className="row-end-1 h-7" />
                            {labels.map((hour, i) => (
                                <React.Fragment key={i}>
                                    <div>
                                        <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs/5 text-gray-400">
                                            {hour}
                                        </div>
                                    </div>
                                    <div />
                                </React.Fragment>
                            ))}
                        </div>

                        <ol
                            style={{
                                gridTemplateRows: `1.75rem repeat(${labels.length * SLOTS_PER_HOUR * SUBROWS_PER_SLOT}, minmax(0, 1fr)) auto`,
                            }}
                            className="col-start-1 col-end-2 row-start-1 z-10 grid grid-cols-1 pr-2">
                            {laidOutEvents.map(({ event: evt, layout }) => {
                                const widthPct = 100 / layout.totalColumns;
                                const leftPct = layout.columnIndex * widthPct;
                                const timeDisplay = formatTime(displayTimeStr(evt));
                                return (
                                    <li
                                        key={displayId(evt)}
                                        style={{
                                            gridRow: `${layout.row} / span ${layout.span}`,
                                            gridColumnStart: 1,
                                        }}
                                        className="relative mt-px pointer-events-none">
                                        <button
                                            onClick={() => onEventClick(evt)}
                                            data-testid={displayTestId(evt)}
                                            style={{
                                                left: `${leftPct}%`,
                                                width: `${widthPct}%`,
                                            }}
                                            className={`pointer-events-auto group absolute inset-y-px flex flex-col overflow-hidden rounded-md text-sm text-left ${
                                                layout.span < SUBROWS_PER_HOUR
                                                    ? "px-2 py-0"
                                                    : "p-2"
                                            } ${timedBlockClasses(evt)}`}>
                                            {layout.span < SUBROWS_PER_HOUR ? (
                                                <p className="font-semibold text-white truncate leading-tight">
                                                    {displayLabel(evt)}
                                                    <span
                                                        className={`ml-1 font-normal ${timedAccentClasses(evt)}`}>
                                                        {timeDisplay}
                                                    </span>
                                                </p>
                                            ) : (
                                                <>
                                                    <p className="font-semibold text-white">
                                                        {displayLabel(evt)}
                                                    </p>
                                                    <p
                                                        className={timedAccentClasses(
                                                            evt,
                                                        )}>
                                                        {timeDisplay}
                                                    </p>
                                                </>
                                            )}
                                        </button>
                                    </li>
                                );
                            })}
                        </ol>
                    </div>
                </div>
            </div>
        </div>
    );
};
